import { PlayCircleFilledOutlined } from "@mui/icons-material";
import { Box, Stack, SxProps } from "@mui/material";
import BasicPlayer from "components/BasicPlayer";
import TEST_ID from "enums/TEST_ID";
import VIDEO_TYPE from "enums/VIDEO_TYPE";
import useGetSignedVideoUrls from "hooks/api/useGetSignedVideoUrls";
import useGetVideoById from "hooks/api/useGetVideoById";
import { useContext, useEffect } from "react";
import ReactPlayer, { ReactPlayerProps } from "react-player";
import { OnProgressProps } from "react-player/base";
import TimelineItem from "types/TimelineItem";
import { getDataTestProp } from "utils/cypressTesting";
import { flexCentered } from "../../theme/base/flex";
import PlayerControls from "./components/PlayerControls";
import TransitionPlayer from "./components/TransitionPlayer";
import TimelinePlayerContext from "./context/TimelinePlayerContext";
import useSeekToAbsoluteTimeHandler from "./hooks/useAbsoluteSeekToHandler";
import useFullscreenHandler from "./hooks/useFullscreenHandler";
import useInitializeTimelinePlayer from "./hooks/useInitializeTimelinePlayer";
import useOnEnded from "./hooks/useOnEnded";
import useOnProgress from "./hooks/useOnProgress";
import useOnReady from "./hooks/useOnReady";
import useSeekToRelativeTimeHandler from "./hooks/useSeekToRelativeTimeHandler";
import useUpdateStateFromAbsoluteTime from "./hooks/useUpdateStateFromAbsoluteTime";

interface Props extends ReactPlayerProps {
  allowDownload?: boolean;
  controls?: boolean;
  isDormant?: boolean;
  showBasicPlayerControls?: boolean;
  onEnded?: () => void;
  onProgress?: (progress: OnProgressProps) => void;
  onReady?: (player: ReactPlayer | undefined) => void;
  progressInterval?: number;
  sx?: SxProps;
  timelineItems: TimelineItem[];
}

export default function TimelinePlayer({
  allowDownload: allowDownloadOverride,
  controls = true,
  isDormant,
  onEnded,
  onProgress,
  onReady,
  playing: _playing,
  progressInterval = 50,
  showBasicPlayerControls = false,
  sx,
  timelineItems,
  ...rest
}: Props) {
  const {
    endTime,
    fullscreen,
    isBeingModified,
    isScrubbing,
    playbackRate,
    player,
    playing,
    setCurrentTimelineItem,
    setPlaybackRate,
    setPlaying,
    startTime,
    timelineItemId,
    timelineItemIndex,
    totalDuration,
    videoId,
  } = useContext(TimelinePlayerContext);

  useInitializeTimelinePlayer(timelineItems, Boolean(_playing));
  useUpdateStateFromAbsoluteTime();
  useSeekToAbsoluteTimeHandler();
  useSeekToRelativeTimeHandler();

  useEffect(() => {
    setCurrentTimelineItem(timelineItems[timelineItemIndex]);
  }, [timelineItems, timelineItemIndex]);

  const containerRef = useFullscreenHandler();
  const _onProgress = useOnProgress(onProgress);
  const _onEnded = useOnEnded(onEnded);
  const _onReady = useOnReady(onReady);
  const currentTimelineItem = timelineItems[timelineItemIndex];
  const signedVideoUrls = useGetSignedVideoUrls(videoId, {
    suspendRequest: Boolean(currentTimelineItem?.signedSourceUrl),
  });

  const PLAYING = playing && !isScrubbing && !isBeingModified;

  const { video } = useGetVideoById(videoId, {
    suspendRequest: Boolean(currentTimelineItem?.signedSourceUrl),
  });

  const signedOriginalUrl =
    currentTimelineItem?.signedSourceUrl || signedVideoUrls.original;

  const resolvedAllowDownload =
    (allowDownloadOverride !== undefined
      ? allowDownloadOverride
      : currentTimelineItem?.allowDownload) ?? true;

  const nativePlayPauseHandlers = showBasicPlayerControls
    ? {
        onPause: () => {
          setPlaying(false);
          rest.onPause?.();
        },
        onPlay: () => {
          setPlaying(true);
          rest.onPlay?.();
        },
      }
    : {};

  return (
    currentTimelineItem && (
      <Stack
        ref={containerRef}
        direction="column"
        sx={{
          display: isDormant ? "none" : "flex",
          flex: 1,
          minHeight: 0,
          position: "relative",
          backgroundColor: "black",
          width: "100%",
          height: "100%",
          ...sx,
        }}
        onClick={
          showBasicPlayerControls ? undefined : () => setPlaying(!playing)
        }
        {...getDataTestProp(TEST_ID.timeline.duration, totalDuration)}
      >
        {!isDormant && (
          <>
            <Box
              sx={{
                backgroundColor: "black",
                flex: 1,
                height: fullscreen ? "96vh" : undefined,
                minHeight: 0,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Box sx={{ position: "absolute", inset: 0 }}>
                {[
                  VIDEO_TYPE.VIDEO,
                  VIDEO_TYPE.CLIP,
                  VIDEO_TYPE.INSIGHT,
                ].includes(currentTimelineItem.type as VIDEO_TYPE) ||
                video?.duration ? (
                  <BasicPlayer
                    key={`${timelineItemId}${timelineItemIndex}${startTime}${endTime}`}
                    allowDownload={resolvedAllowDownload}
                    controls={showBasicPlayerControls && controls}
                    endTime={endTime}
                    onEnded={_onEnded}
                    onProgress={_onProgress}
                    onReady={_onReady}
                    onPlaybackRateChange={setPlaybackRate}
                    playbackRate={playbackRate}
                    playing={PLAYING}
                    progressInterval={progressInterval}
                    startTime={startTime}
                    videoId={videoId}
                    transcodeOverride={currentTimelineItem.signedSourceUrl}
                    {...rest}
                    {...nativePlayPauseHandlers}
                  />
                ) : (
                  <TransitionPlayer
                    playing={PLAYING}
                    onEnded={_onEnded}
                    sx={
                      signedOriginalUrl
                        ? {
                            backgroundImage: `url(${signedOriginalUrl})`,
                            backgroundSize: "contain",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                          }
                        : undefined
                    }
                  />
                )}
              </Box>
              {!showBasicPlayerControls && (
                <Box
                  sx={{
                    position: "absolute",
                    cursor: "pointer",
                    height: "100%",
                    width: "100%",
                    transition: "opacity 0.25s",
                    opacity: playing ? 0 : 1,
                    ...flexCentered,
                    display: player ? "flex" : "none",
                  }}
                >
                  <PlayCircleFilledOutlined
                    fontSize="large"
                    sx={{ color: "common.white" }}
                  />
                </Box>
              )}
            </Box>
            {!showBasicPlayerControls && (
              <PlayerControls
                showControls={controls}
                allowDownload={resolvedAllowDownload}
                sx={{
                  backgroundColor: "#eee",
                  flexShrink: 0,
                  p: ".5rem 1rem",
                  width: "100%",
                  zIndex: 1,
                  height: fullscreen ? "4vh" : undefined,
                }}
              />
            )}
          </>
        )}
      </Stack>
    )
  );
}
